import React, { Fragment, useCallback } from 'react'
import PropTypes from 'prop-types'
import { HeartIcon, PeopleIconTwo, StarIcon } from 'secondstep-components'
import { domains } from 'config/env'
import NavigationHelper from 'layers/navigation/navigationHelper'
import { buildK8ReportSitePath } from 'components/ReportsPageHeader/flag.on/constants'
import { K8_REPORT_UNIT_SECTION } from 'pages/LessonCompletionReport/flag.on/constants'
import {
  RecentActivitiesMetric,
  RecentActivitiesMetricCount,
  RecentActivitiesMetricDescription,
  RecentActivitiesMetricDivider,
  RecentActivitiesMetricIcon,
  RecentActivitiesMetricIconHeart,
  RecentActivitiesMetricLabel,
  RecentActivitiesMetricRow,
  RecentActivitiesPanel,
  RecentActivitiesYellowDot,
} from '../component.styles'
import {
  RecentActivitiesDoneStarIcon,
  RecentActivitiesLink,
  RecentActivitiesLinkWrapper,
  RecentActivitiesMetricContent,
  RecentActivitiesPrimaryButton,
  RecentActivitiesSecondaryButton,
} from './component.styles'
import {
  RECENT_ACTIVITIES_ALL_CLASSES_DESCRIPTION,
  RECENT_ACTIVITIES_ALL_CLASSES_LABEL,
  RECENT_ACTIVITIES_LESSON_14_DAYS_DESCRIPTION,
  RECENT_ACTIVITIES_NO_LESSON_DESCRIPTION,
  RECENT_ACTIVITIES_STAFF_LOGIN_DESCRIPTION,
  SEE_CLASSES_BUTTON_TEXT,
  USER_MANAGEMENT_PATH,
  VIEW_CLASSES_LINK_TEXT,
  VIEW_STAFF_BUTTON_TEXT,
} from '../constants'

const buildMetrics = ({
  classesWithoutLesson,
  onViewClassesClick,
  onViewStaffClick,
  recentLessonClasses,
  staffLoggedIn,
  totalClassesCount,
}) => {
  const allClassesHaveStarted =
    classesWithoutLesson === 0 && totalClassesCount > 0
  const metrics = []

  if (!allClassesHaveStarted) {
    metrics.push({
      buttonText: SEE_CLASSES_BUTTON_TEXT,
      buttonVariant: 'primary',
      count: classesWithoutLesson,
      description: RECENT_ACTIVITIES_NO_LESSON_DESCRIPTION,
      iconType: 'yellowDot',
      label: 'classes',
      onButtonClick: onViewClassesClick,
    })
  }

  metrics.push({
    buttonText: SEE_CLASSES_BUTTON_TEXT,
    buttonVariant: allClassesHaveStarted ? 'primary' : 'secondary',
    count: recentLessonClasses,
    description: RECENT_ACTIVITIES_LESSON_14_DAYS_DESCRIPTION,
    iconType: 'heart',
    label: 'classes',
    onButtonClick: onViewClassesClick,
  })

  metrics.push({
    buttonText: VIEW_STAFF_BUTTON_TEXT,
    buttonVariant: 'secondary',
    count: staffLoggedIn,
    description: RECENT_ACTIVITIES_STAFF_LOGIN_DESCRIPTION,
    iconType: 'staff',
    label: 'Staff',
    onButtonClick: onViewStaffClick,
  })

  if (allClassesHaveStarted) {
    metrics.push({
      buttonText: VIEW_CLASSES_LINK_TEXT,
      buttonVariant: 'link',
      description: RECENT_ACTIVITIES_ALL_CLASSES_DESCRIPTION,
      iconType: 'done',
      label: RECENT_ACTIVITIES_ALL_CLASSES_LABEL,
      onButtonClick: onViewClassesClick,
    })
  }

  return metrics
}

const MetricIcon = ({ iconType }) => {
  if (iconType === 'yellowDot') {
    return (
      <RecentActivitiesMetricIcon>
        <RecentActivitiesYellowDot />
      </RecentActivitiesMetricIcon>
    )
  }

  if (iconType === 'heart') {
    return (
      <RecentActivitiesMetricIconHeart>
        <HeartIcon />
      </RecentActivitiesMetricIconHeart>
    )
  }

  if (iconType === 'done') {
    return (
      <RecentActivitiesMetricIcon>
        <RecentActivitiesDoneStarIcon>
          <StarIcon />
        </RecentActivitiesDoneStarIcon>
      </RecentActivitiesMetricIcon>
    )
  }

  return (
    <RecentActivitiesMetricIcon>
      <PeopleIconTwo />
    </RecentActivitiesMetricIcon>
  )
}

MetricIcon.propTypes = {
  iconType: PropTypes.oneOf(['yellowDot', 'heart', 'staff', 'done']).isRequired,
}

const MetricAction = ({ buttonText, buttonVariant, onButtonClick }) => {
  if (buttonVariant === 'link') {
    return (
      <RecentActivitiesLinkWrapper>
        <RecentActivitiesLink onClick={onButtonClick}>
          {buttonText}
        </RecentActivitiesLink>
      </RecentActivitiesLinkWrapper>
    )
  }

  if (buttonVariant === 'primary') {
    return (
      <RecentActivitiesPrimaryButton onClick={onButtonClick}>
        {buttonText}
      </RecentActivitiesPrimaryButton>
    )
  }

  return (
    <RecentActivitiesSecondaryButton onClick={onButtonClick}>
      {buttonText}
    </RecentActivitiesSecondaryButton>
  )
}

MetricAction.propTypes = {
  buttonText: PropTypes.string.isRequired,
  buttonVariant: PropTypes.oneOf(['primary', 'secondary', 'link']).isRequired,
  onButtonClick: PropTypes.func,
}

const RecentActivitiesMetricCard = ({
  buttonText,
  buttonVariant,
  count,
  description,
  iconType,
  label,
  onButtonClick,
}) => (
  <RecentActivitiesMetric>
    <RecentActivitiesMetricContent isDone={iconType === 'done'}>
      <RecentActivitiesMetricRow>
        <MetricIcon iconType={iconType} />
        {count != null && (
          <RecentActivitiesMetricCount>{count}</RecentActivitiesMetricCount>
        )}
        <RecentActivitiesMetricLabel>{label}</RecentActivitiesMetricLabel>
      </RecentActivitiesMetricRow>
      <RecentActivitiesMetricDescription>
        {description}
      </RecentActivitiesMetricDescription>
    </RecentActivitiesMetricContent>
    <MetricAction
      buttonText={buttonText}
      buttonVariant={buttonVariant}
      onButtonClick={onButtonClick}
    />
  </RecentActivitiesMetric>
)

RecentActivitiesMetricCard.propTypes = {
  buttonText: PropTypes.string.isRequired,
  buttonVariant: PropTypes.oneOf(['primary', 'secondary', 'link']).isRequired,
  count: PropTypes.number,
  description: PropTypes.string.isRequired,
  iconType: PropTypes.oneOf(['yellowDot', 'heart', 'staff', 'done']).isRequired,
  label: PropTypes.string.isRequired,
  onButtonClick: PropTypes.func,
}

const PlaybookRecentActivitiesView = ({
  navigationEvent,
  numClassesMarkedLastPeriod,
  numClassesNotMarkedLesson,
  siteId,
  siteUserStats,
  totalClassesCount,
}) => {
  const handleViewStaffClick = useCallback(() => {
    if (!navigationEvent || siteId == null) return

    const userManagementUrl = `${domains.ADMIN_DOMAIN}${USER_MANAGEMENT_PATH}/${siteId}`
    navigationEvent(userManagementUrl, NavigationHelper.types.OPEN)
  }, [navigationEvent, siteId])

  const handleViewClassesClick = useCallback(() => {
    if (!navigationEvent || siteId == null) return

    const reportsUrl = buildK8ReportSitePath(
      K8_REPORT_UNIT_SECTION.CORE_UNITS,
      siteId,
    )
    navigationEvent(reportsUrl, NavigationHelper.types.PUSH)
  }, [navigationEvent, siteId])

  const metrics = buildMetrics({
    classesWithoutLesson: numClassesNotMarkedLesson ?? 0,
    onViewClassesClick: handleViewClassesClick,
    onViewStaffClick: handleViewStaffClick,
    recentLessonClasses: numClassesMarkedLastPeriod ?? 0,
    staffLoggedIn: siteUserStats?.usersLoggedInLast7Days ?? 0,
    totalClassesCount: totalClassesCount ?? 0,
  })

  return (
    <RecentActivitiesPanel data-testid="playbook-recent-activities-panel">
      {metrics.map((metric, index) => (
        <Fragment key={`${metric.iconType}-${metric.label}`}>
          {index > 0 && <RecentActivitiesMetricDivider />}
          <RecentActivitiesMetricCard {...metric} />
        </Fragment>
      ))}
    </RecentActivitiesPanel>
  )
}

PlaybookRecentActivitiesView.propTypes = {
  navigationEvent: PropTypes.func,
  numClassesMarkedLastPeriod: PropTypes.number,
  numClassesNotMarkedLesson: PropTypes.number,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  siteUserStats: PropTypes.shape({
    usersLoggedInLast7Days: PropTypes.number,
  }),
  totalClassesCount: PropTypes.number,
}

export default PlaybookRecentActivitiesView
